import { discountTypes } from 'data-layer-cr/dist/constants';
import {
  defaultUnit,
  getDiscountedTotal,
} from 'data-layer-cr/dist/functions/quotations';
import {
  GeneralCatalogItem,
  Quotation,
  factory,
} from 'data-layer-cr/dist/models';
import { format } from 'date-fns';
import { CreditCardData, dateFormat } from 'design-system';

import { individuals, profiles } from 'src/constants/profileTypes';
import { EmissionPolicyState } from 'src/store/emissionPolicy/slice/emissionPolicySlice';
import { TextValuesPayload } from 'src/store/inspectionForm/slice/inspectionFormSlice';

import ClaveData from '../../../helperTypes/ClaveData';
import EmissionRequest, {
  EmissionDocument,
  IssuerInformation,
} from '../../EmissionRequest';

import ACHData from '../../../helperTypes/ACHData';

import {
  convertToEmissionClient,
  createPaymentIssuer,
  makeEmissionDocument,
  makeFinancingInformation,
  makePaymentConditions,
  makeUnitInformation,
  parseCreditCard,
} from './functions';

const { digestDateForBackend } = dateFormat;

export type MakeEmissionRequestParams = {
  quotation: Quotation;
  formState: EmissionPolicyState;
  bankList: GeneralCatalogItem[];
  creditCardBankList: GeneralCatalogItem[];
  inspectionTextValues: TextValuesPayload;
  uniqueRequestId: string;
  sessionEmails: string[];
  creditCardData: CreditCardData;
  achData: ACHData;
  claveData: ClaveData;
  sessionUserName: string;
  sessionUserId: string;
  sessionLicense: string;
  sessionRole: string;
  /** Optional catalog arrays for fallback name resolution when name fields are empty */
  cities?: GeneralCatalogItem[];
  townships?: GeneralCatalogItem[];
};

export function makeEmissionRequest({
  quotation,
  formState,
  bankList,
  creditCardBankList,
  inspectionTextValues,
  uniqueRequestId,
  creditCardData,
  achData,
  sessionEmails,
  sessionUserName,
  sessionUserId,
  sessionLicense,
  sessionRole,
  claveData,
  cities,
  townships,
}: MakeEmissionRequestParams): EmissionRequest {
  const {
    paymentFields,
    dataFields,
    identificationBase64,
    identificationFileName,
    identificationOptionalBase64,
    identificationOptionalFileName,
    newCarInvoiceBase64,
    newCarInvoiceFilename,
    paymentIssuerSource,
    paymentIssuer,
    finalBeneficiary,
    additionalDocumentACHBase64,
    additionalDocumentACHFileName,
    legalBeneficiaries,
    // totals
    paymentTotal,
    electronicPaymentTotal,
    autoPxPaymentTotal,
  } = formState;

  const effectiveDate = dataFields.assignDateSignature
    ? format(new Date(), 'dd/MM/yyyy')
    : dataFields.effectiveDate;
  const validSinceDate = paymentFields.policyStartDate
    ? paymentFields.policyStartDate
    : effectiveDate;
  const { type, paymentQuantityType } = paymentFields;
  const { driver } = quotation.units[0].unit;
  const emissionClient = convertToEmissionClient({
    quotation,
    driver,
    dataFields,
    beneficiary: finalBeneficiary,
    legalBeneficiaries,
    cities,
    townships,
  });

  let usedPaymentAmount = getDiscountedTotal({
    /** Use recorded totals in payment step */
    total: {
      ...defaultUnit(quotation).total,
      grandTotal: paymentTotal,
      discounts: [
        {
          ...factory.makeEmptyDiscount(),
          id: discountTypes.ELECTRONIC_DISCOUNT_ID,
          total: electronicPaymentTotal,
        },
        {
          ...factory.makeEmptyDiscount(),
          id: discountTypes.AUTOPX_DISCOUNT_ID,
          total: autoPxPaymentTotal,
        },
      ],
    },
    quotation,
    paymentType: type,
    paymentQuantityType,
  }).grandTotal;

  const quotationProfileType = quotation.quotationProfile.type ?? '';

  if (individuals.includes(quotationProfileType)) {
    usedPaymentAmount = paymentFields.paymentAmount;
  }
  // Decide which additional documents must be sent
  const additionalDocuments: EmissionDocument[] = [];
  if (identificationBase64) {
    additionalDocuments.push(
      makeEmissionDocument(
        'cedula',
        identificationBase64,
        identificationFileName,
      ),
    );
  }

  if (identificationOptionalBase64) {
    additionalDocuments.push(
      makeEmissionDocument(
        'cedula-optional',
        identificationOptionalBase64,
        identificationOptionalFileName,
      ),
    );
  }

  if (newCarInvoiceBase64) {
    if (quotationProfileType === profiles.RETENCION) {
      additionalDocuments.push(
        makeEmissionDocument(
          'certificado_poliza',
          newCarInvoiceBase64,
          newCarInvoiceFilename,
        ),
      );
    } else {
      additionalDocuments.push(
        makeEmissionDocument(
          'proforma',
          newCarInvoiceBase64,
          newCarInvoiceFilename,
        ),
      );
    }
  }

  if (additionalDocumentACHBase64) {
    additionalDocuments.push(
      makeEmissionDocument(
        'achDocument',
        additionalDocumentACHBase64,
        additionalDocumentACHFileName,
      ),
    );
  }

  const firstPayment = parseCreditCard(
    usedPaymentAmount,
    paymentFields,
    dataFields,
    creditCardData,
    achData,
    creditCardBankList,
    claveData,
  );

  /** Parse session emails. The backend is expecting an empty array if there are no additional emails */
  const additionalEmails = sessionEmails.filter((email) => email !== '');
  const issuedBy: null | IssuerInformation =
    sessionUserId === ''
      ? null
      : {
          id: sessionUserId,
          role: sessionRole,
          license: sessionLicense,
          email: additionalEmails[0],
          name: sessionUserName,
        };
  // Build the request
  return {
    productId: quotation.productId,
    quotationId: quotation.id.toString(),
    unitInformation: makeUnitInformation(inspectionTextValues),
    firstPayment,
    paymentConditions: makePaymentConditions(paymentFields),
    client: emissionClient,
    paymentIssuer: createPaymentIssuer(
      emissionClient,
      paymentIssuerSource,
      paymentIssuer,
    ),
    aditionalDocuments: additionalDocuments,
    financingInformation: makeFinancingInformation(dataFields, bankList),
    createdBy: '',
    validSince: digestDateForBackend(validSinceDate),
    emissionRequestId: uniqueRequestId,
    packageOption: quotation.packageOption,
    additionalEmails,
    issuedBy,
    assignDateSignature: dataFields.assignDateSignature,
    units: quotation.units,
  };
}

export default makeEmissionRequest;
